import { useRetailerCoreWithSelector } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/RetailerCoreProvider";
import { isRetailerCreatedOver30DaysOld } from "@faire/deprecated-retailer-visitor-shared/lib/isRetailerCreatedOver30DaysOld";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { flashSaleSeasonalWarehouseSaleIsOn } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSeasonalWarehouseSale";
import { useUserStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useUserStore";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

interface RetailerFlashSale {
  showFlashSales: boolean;
  showFlashSalesAsUnavailable: boolean;
  showWarehouseFlashSale: boolean;
}

const RetailerFlashSaleContext = createContext<
  StoreApi<RetailerFlashSale> | undefined
>(undefined);

export const RetailerFlashSaleProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);
  const { userCanPlaceOrder } = useUserStore();

  const store = useMemo(
    () =>
      createStore<RetailerFlashSale>(() => {
        const retailerCountry = (retailer?.country ??
          initialRetailer?.country) as ISO3166Alpha3;
        const isAmerican =
          retailerCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;

        const showFlashSales =
          userCanPlaceOrder &&
          !!retailer &&
          !retailer.hide_redistribution_channels &&
          (isAmerican || retailerCountry === undefined) &&
          isRetailerCreatedOver30DaysOld(
            retailer.created_at ?? initialRetailer?.created_at
          );

        const showFlashSalesAsUnavailable =
          userCanPlaceOrder &&
          !!retailer?.hide_redistribution_channels &&
          !!retailer?.show_flash_sales_mix_and_match_as_unavailable &&
          (isAmerican || retailerCountry === undefined);

        const showWarehouseFlashSale =
          showFlashSales && flashSaleSeasonalWarehouseSaleIsOn();

        return {
          showFlashSales,
          showFlashSalesAsUnavailable,
          showWarehouseFlashSale,
        };
      }),
    [
      retailer,
      initialRetailer?.country,
      initialRetailer?.created_at,
      userCanPlaceOrder,
    ]
  );

  return (
    <RetailerFlashSaleContext.Provider value={store}>
      {children}
    </RetailerFlashSaleContext.Provider>
  );
};

export const useFlashSale = () => {
  const context = useContext(RetailerFlashSaleContext);
  if (!context) {
    throw new Error(
      "RetailerFlashSaleContext not found. Must be used within RetailerFlashSaleProvider"
    );
  }
  return useStore(context);
};
